// PotreeViewer3 리팩토링 - 출발점 레이캐스트/교차점 계산 모듈
// 지침: PotreeViewer3.jsx의 handlePointCloudPick은 그대로 두고,
//       레이캐스트/교차점/출발점 메쉬 생성 로직만 이 유틸로 옮긴다.

import * as THREE from "three";
import * as turf from "@turf/turf";
import { toWGS84, toEPSG5186 } from "./pathConnector";

// ---------------------------------------------------------------------------
// getClosestPointWGS84
// ---------------------------------------------------------------------------
// 원본: PotreeViewer3.jsx L450–510
// 변경 사항:
// - potreeViewer, selectedPointMode를 클로저 대신 인자로 받도록 명시화

export const getClosestPointWGS84 = (event, potreeViewer, selectedPointMode) => {
  const rayCastFunction = (event) => {
    const canvas = potreeViewer.renderer.domElement;
    const camera = potreeViewer.scene.getActiveCamera();
    if (!selectedPointMode) return;

    const raycaster = new THREE.Raycaster();

    const rect = canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // 2) Raycaster 설정
    raycaster.setFromCamera(mouse, camera);
    const ray = raycaster.ray;

    // 3) 포인트클라우드 Octree들 대상
    const candidates = potreeViewer.scene.scenePointCloud.children;

    // 4) 레이와 교차하는 노드 찾기
    const hits = raycaster.intersectObjects(candidates, true);

    if (hits.length === 0) return null;

    // 가장 앞에 있는 노드
    const hitNode = hits[0].object;

    // 5) 해당 노드 내부의 실제 포인트들 접근
    const positions = hitNode.geometry.attributes.position.array;
    const numPoints = positions.length / 3;

    // 6) 가장 가까운 포인트 계산
    let minDist = Infinity;
    const closest = new THREE.Vector3();

    for (let i = 0; i < numPoints; i++) {
      const x = positions[i * 3 + 0];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];

      const p = new THREE.Vector3(x, y, z).applyMatrix4(hitNode.matrixWorld);

      // 레이에서 포인트까지의 가장 짧은 거리
      const dist = ray.distanceToPoint(p);

      if (dist < minDist) {
        minDist = dist;
        closest.copy(p);
      }
    }

    return closest;
  };

  const closestPoint = rayCastFunction(event);
  if (!closestPoint) return null;
  return {
    wgs84: toWGS84([closestPoint.x, closestPoint.y]),
    point3d: closestPoint, // 3D 좌표 (x, y, z) 포함
  };
};

// ---------------------------------------------------------------------------
// getClosetIntersection
// ---------------------------------------------------------------------------
// 원본: PotreeViewer3.jsx L526–578

export const getClosetIntersection = (pointWGS84, polygonOriginal) => {
  const P = [pointWGS84[0], pointWGS84[1]]; // 클릭한 포인터 좌표
  const polygon = polygonOriginal.geometry.coordinates[0]; // 측정 영역 폴리곤
  const directions = []; // 방향 정의
  for (let deg = 0; deg < 360; deg += 5) {
    directions.push((deg * Math.PI) / 180); // degree → radian
  }
  const intersections = []; // 교차점 수집

  const edges = []; // 테두리 정의
  for (let i = 0; i < polygon.length - 1; i++) {
    edges.push(turf.lineString([polygon[i], polygon[i + 1]]));
  }

  directions.forEach((angle) => {
    const dir = [Math.cos(angle), Math.sin(angle)]; // 방향 정의

    const ray = turf.lineString([
      // 레이 정의
      P,
      [P[0] + dir[0] * 9999, P[1] + dir[1] * 9999],
    ]);

    // 각 테두리와 레이 교차점 수집
    edges.forEach((edge, index) => {
      const hit = turf.lineIntersect(ray, edge);
      if (hit.features.length > 0) {
        intersections.push({
          point: hit.features[0].geometry.coordinates,
          edgeIndex: index,
        });
      }
    });
  });

  let best = null;
  let bestDist = Infinity;

  // 가장 가까운 교차점 찾기
  intersections.forEach((hit) => {
    const pt = hit.point;
    const dx = pt[0] - P[0];
    const dy = pt[1] - P[1];
    const d2 = dx * dx + dy * dy;

    if (d2 < bestDist) {
      bestDist = d2;
      best = hit;
    }
  });

  return best;
};

// ---------------------------------------------------------------------------
// createStartPointMesh
// ---------------------------------------------------------------------------
// 원본: PotreeViewer3.jsx L580–603
// 변경 사항:
// - potreeViewer, selectedStartPointRef, getMinZ를 인자로 받도록 명시화

export const createStartPointMesh = (
  pointWGS84,
  potreeViewer,
  selectedStartPointRef,
  getMinZ
) => {
  if (!potreeViewer) return;
  const existingMesh = potreeViewer.scene.scene.getObjectByName("start_point");
  if (existingMesh) {
    existingMesh.geometry?.dispose?.();
    existingMesh.material?.dispose?.();
    potreeViewer.scene.scene.remove(existingMesh);
  }

  const point = toEPSG5186(pointWGS84);
  // 출발점의 z값 기준 사용 (없으면 최소값 사용)
  const baseZ = selectedStartPointRef.current?.z ?? getMinZ();
  const offsetZ = baseZ + 2.5; // 출발점 z값 + 2.5m
  const coneGeo = new THREE.ConeGeometry(0.5, 1, 16);
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
  });
  const mesh = new THREE.Mesh(coneGeo, material);
  mesh.name = "start_point";
  mesh.position.set(point[0], point[1], offsetZ);
  mesh.rotation.x = -Math.PI / 2;
  potreeViewer.scene.scene.add(mesh);
};


